import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Calendar,
  MapPin,
  ArrowLeft,
  Activity,
  Target,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { SectionCard } from "@/components/ui/SectionCard";
import { MetricTile } from "@/components/ui/MetricTile";
import { AgeGroupBadge } from "@/components/ui/AgeGroupBadge";
import { HomeworkCard } from "@/components/parent/HomeworkCard";
import { useState } from "react";

interface KidDashboardData {
  kid: {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    dateOfBirth: string;
    age: number;
    ageGroup: string;
    location: string;
    profileImage: string | null;
  };
  fitness: any;
  batting: any;
  bowling: any;
  fielding: any;
  discipline: any;
  attendance: {
    total: number;
    attended: number;
    missed: number;
    lastSessionDate: string | null;
  };
  upcomingSessions: Array<{
    id: number;
    title: string;
    description: string | null;
    sessionType: string;
    location: string;
    startTime: string;
    endTime: string;
    coachName: string | null;
    availabilityStatus: "yes" | "no" | "maybe" | null;
  }>;
  recentNotes: Array<{
    id: number;
    content: string;
    noteDate: string;
    category: string | null;
    coachName: string | null;
  }>;
}

export default function KidDashboard() {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [updatingSessionId, setUpdatingSessionId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [`/api/parent/kids/${kidId}/dashboard`],
    queryFn: async () => {
      console.log(`[KID DASHBOARD UI] Fetching dashboard for kidId: ${kidId}`);
      try {
        const response = await api.get(`/api/parent/kids/${kidId}/dashboard`);
        console.log(`[KID DASHBOARD UI] ✅ Success:`, { 
          success: response.success, 
          hasData: !!response.data,
          kidName: response.data?.kid?.fullName 
        });
        return response;
      } catch (err: any) {
        console.error(`[KID DASHBOARD UI] ❌ Error:`, {
          status: err.response?.status,
          statusText: err.response?.statusText,
          errorCode: err.response?.data?.errorCode,
          message: err.response?.data?.message,
          fullError: err
        });
        throw err;
      }
    },
    enabled: !!kidId,
  });

  const dashboardData: KidDashboardData | null = data?.data || null;

  // Mutation for updating availability
  const updateAvailability = useMutation({
    mutationFn: async ({ sessionId, status }: { sessionId: number; status: "yes" | "no" | "maybe" }) => {
      console.log(`[AVAILABILITY] Updating session ${sessionId} to ${status} for kid ${kidId}`);
      const response = await api.post(`/api/parent/sessions/${sessionId}/availability`, {
        kidId: parseInt(kidId!),
        status,
      });
      return response;
    },
    onMutate: async ({ sessionId, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [`/api/parent/kids/${kidId}/dashboard`] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`/api/parent/kids/${kidId}/dashboard`]);

      // Optimistically update to the new value
      queryClient.setQueryData([`/api/parent/kids/${kidId}/dashboard`], (old: any) => {
        if (!old?.data?.upcomingSessions) return old;
        
        return {
          ...old,
          data: {
            ...old.data,
            upcomingSessions: old.data.upcomingSessions.map((session: any) =>
              session.id === sessionId
                ? { ...session, availabilityStatus: status }
                : session
            ),
          },
        };
      });

      return { previousData };
    },
    onSuccess: () => {
      // Refetch dashboard data to get updated availability from server
      queryClient.invalidateQueries({ queryKey: [`/api/parent/kids/${kidId}/dashboard`] });
      setUpdatingSessionId(null);
    },
    onError: (error: any, variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData([`/api/parent/kids/${kidId}/dashboard`], context.previousData);
      }
      console.error("[AVAILABILITY] Error updating:", error);
      setUpdatingSessionId(null);
      alert("Failed to update availability. Please try again.");
    },
  });

  const handleAvailabilityClick = (sessionId: number, status: "yes" | "no" | "maybe") => {
    setUpdatingSessionId(sessionId);
    updateAvailability.mutate({ sessionId, status });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="parent-kid-dashboard-page">
        <main data-testid="kid-dashboard-metrics">
          <div className="flex items-center gap-3" data-testid="loading-dashboard">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
            <span className="text-slate-700 font-medium">Loading dashboard...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error || !dashboardData) {
    // Extract error details
    const errorResponse = (error as any)?.response?.data;
    const errorCode = errorResponse?.errorCode || 'UNKNOWN_ERROR';
    const errorMessage = errorResponse?.message || (error instanceof Error ? error.message : 'Failed to load dashboard. Please try again later.');
    
    // Determine specific error test ID
    let errorTestId = "dashboard-error-generic";
    if (errorCode === "KID_NOT_OWNED_BY_PARENT") {
      errorTestId = "dashboard-error-kid-not-owned";
    } else if (errorCode === "NOT_AUTHENTICATED") {
      errorTestId = "dashboard-error-not-authenticated";
    } else if (errorCode === "NOT_PARENT") {
      errorTestId = "dashboard-error-not-parent";
    }
    
    console.error("[KID DASHBOARD UI] Error loading dashboard:", { error, errorCode, errorMessage, kidId });
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="parent-kid-dashboard-error">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8" data-testid={errorTestId}>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                <User className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900" data-testid="error-heading">Error Loading Dashboard</h2>
              <p className="text-sm text-slate-600" data-testid="error-message">
                {errorMessage}
              </p>
              {errorCode && (
                <p className="text-xs text-slate-500" data-testid="error-code">
                  Error Code: {errorCode}
                </p>
              )}
              <Button 
                onClick={() => navigate("/parent/kids")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium h-11 rounded-full"
                data-testid="btn-back-to-kids"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Kids List
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { kid, attendance, upcomingSessions, recentNotes, batting, bowling, fielding, discipline } = dashboardData;

  const attendancePercentage = attendance.total > 0
    ? Math.round((attendance.attended / attendance.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="parent-kid-dashboard-page">
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="kid-dashboard-metrics">
        {/* Header */}
        <div className="space-y-4" data-testid="kid-dashboard-header">
          <Button 
            variant="outline" 
            onClick={() => navigate("/parent/kids")}
            className="border-slate-200 hover:bg-slate-100 text-slate-700"
            data-testid="btn-back-to-kids"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My Kids
          </Button>
          
          {/* Kid Info Header */}
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="kid-name">
              {kid.fullName}
            </h1>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Age {kid.age}</span>
              <span>•</span>
              <AgeGroupBadge ageGroup={kid.ageGroup} testId="kid-age-group-badge" />
              <span>•</span>
              <span>{kid.location}</span>
            </div>
          </div>
        </div>

        {/* Homework Card - Show at top when homework exists */}
        <HomeworkCard kidId={kidId!} />

        {/* Metrics Section */}
        <SectionCard title="Overview" testId="kid-dashboard-overview">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MetricTile
              label="Sessions attended"
              value={attendance.attended}
              variant="success"
              testId="kid-metric-sessions"
            />
            <MetricTile
              label="Weekly average"
              value={attendance.attended > 0 ? `${Math.min(Math.round(attendance.attended / 4), 3)}/week` : "0/week"}
              variant="info"
              testId="kid-metric-weekly"
            />
            <MetricTile
              label="Last session"
              value={attendance.lastSessionDate ? format(new Date(attendance.lastSessionDate), "MMM dd") : "N/A"}
              variant="default"
              testId="kid-metric-last-session"
            />
          </div>
        </SectionCard>

        {/* Skills Snapshot Section */}
        <SectionCard title="Skills Snapshot" testId="kid-dashboard-skills">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricTile
              label="Batting"
              value={batting ? `${Math.round((batting.footwork + batting.shotSelection + batting.batSwingPath + batting.balancePosture) / 4 * 20)}%` : 'N/A'}
              variant="info"
              testId="kid-skill-batting"
            />
            <MetricTile
              label="Bowling"
              value={bowling ? `${Math.round((bowling.runUpRhythm + bowling.loadGather + bowling.releaseConsistency + bowling.lineLength) / 4 * 20)}%` : 'N/A'}
              variant="success"
              testId="kid-skill-bowling"
            />
            <MetricTile
              label="Fielding"
              value={fielding ? `${Math.round((fielding.throwingAccuracy + fielding.catching + fielding.groundFielding) / 3 * 20)}%` : 'N/A'}
              variant="warning"
              testId="kid-skill-fielding"
            />
            <MetricTile
              label="Fitness"
              value={dashboardData.fitness ? `${Math.round((dashboardData.fitness.speed + dashboardData.fitness.agility + dashboardData.fitness.endurance) / 3 * 20)}%` : 'N/A'}
              variant="info"
              testId="kid-skill-fitness"
            />
            <MetricTile
              label="Mindset"
              value={discipline ? `${Math.round((discipline.focus + discipline.teamwork + discipline.coachability) / 3 * 20)}%` : 'N/A'}
              variant="success"
              testId="kid-skill-mindset"
            />
          </div>
        </SectionCard>

        {/* Recent Drills Section */}
        <SectionCard title="Recent Drills" testId="kid-dashboard-drills">
          {recentNotes && recentNotes.length > 0 ? (
            <div className="space-y-3">
              {recentNotes.slice(0, 3).map((note) => (
                <div
                  key={note.id}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-4 hover:bg-slate-100 transition-colors"
                  data-testid="kid-drill-item"
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-slate-900">
                      {note.category || 'General Drill'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {format(new Date(note.noteDate), "MMM dd, yyyy")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{note.content}</p>
                  {note.coachName && (
                    <p className="text-xs text-slate-500 mt-2">— {note.coachName}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              No recent drills yet
            </p>
          )}
        </SectionCard>

        {/* Upcoming Sessions */}
        <SectionCard title="Upcoming Sessions" testId="kid-dashboard-upcoming">
          {upcomingSessions.length === 0 ? (
            <p className="text-slate-500 text-center py-8" data-testid="no-sessions">
              No upcoming sessions scheduled
            </p>
          ) : (
            <div className="space-y-4">
              {upcomingSessions.map((session) => {
                const status = session.availabilityStatus;
                return (
                  <div
                    key={session.id}
                    className="bg-slate-50 border border-slate-200 rounded-2xl p-4 hover:bg-slate-100 hover:border-slate-300 transition-all"
                    data-testid="session-row"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                        <div className="space-y-1 flex-1">
                          <div className="font-semibold text-slate-900" data-testid="session-title">{session.title}</div>
                          <div className="text-sm text-slate-600" data-testid="session-time">
                            {format(new Date(session.startTime), "MMM dd, yyyy • h:mm a")}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="h-4 w-4" />
                            {session.location}
                          </div>
                        </div>
                        <Badge 
                          variant="outline" 
                          className="self-start bg-slate-100 text-slate-700 border-slate-300"
                        >
                          {session.sessionType}
                        </Badge>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-slate-600">Your response:</span>
                          {!status && (
                            <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">
                              <Clock className="h-3 w-3 mr-1" />
                              Not Answered
                            </Badge>
                          )}
                          {status === "yes" && (
                            <Badge className="text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`status-yes-${session.id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Coming
                            </Badge>
                          )}
                          {status === "no" && (
                            <Badge variant="destructive" className="text-xs" data-testid={`status-no-${session.id}`}>
                              <XCircle className="h-3 w-3 mr-1" />
                              Can't Attend
                            </Badge>
                          )}
                          {status === "maybe" && (
                            <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600" data-testid={`status-maybe-${session.id}`}>
                              <Clock className="h-3 w-3 mr-1" />
                              Not Sure
                            </Badge>
                          )}
                        </div>

                        {/* Availability Response Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleAvailabilityClick(session.id, "yes")}
                            disabled={updatingSessionId === session.id}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                              status === "yes"
                                ? "bg-emerald-600 text-white"
                                : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                            }`}
                            data-testid={`btn-coming-${session.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Coming
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(session.id, "maybe")}
                            disabled={updatingSessionId === session.id}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                              status === "maybe"
                                ? "bg-slate-600 text-white"
                                : "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                            }`}
                            data-testid={`btn-maybe-${session.id}`}
                          >
                            <Clock className="h-4 w-4" />
                            Not Sure
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(session.id, "no")}
                            disabled={updatingSessionId === session.id}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                              status === "no"
                                ? "bg-red-600 text-white"
                                : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                            }`}
                            data-testid={`btn-cant-attend-${session.id}`}
                          >
                            <XCircle className="h-4 w-4" />
                            Can't Attend
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

      </main>
    </div>
  );
}
